import { useId } from 'react'
import TimeField from './TimeField.jsx'
import { formatShort, formatTime, hoursWindowError } from '../lib/dates.js'

/**
 * The whole day, or a window within it — in two pieces, because they belong in two places.
 *
 * The choice leads the dialog: it is the first thing anybody decides and everything below it
 * follows. The times belong under the dates, next to the days they apply to. Splitting the
 * component is what lets the form read in the order the answer is actually given, without
 * either half having to know where the other one went.
 *
 * Both take the same `{ partial, startTime, endTime }` object and the same `onChange`, so the
 * parent keeps one piece of state and neither half can drift from the other.
 *
 * All day is the default and the common case. When the times do appear they open EMPTY rather
 * than pre-filled: a default window that happened to be plausible would get saved unread, and
 * an 8-to-4 nobody typed is worse than a dialog that will not submit until somebody does.
 *
 * Times stay 'HH:MM' strings throughout — nothing here makes a Date.
 */

/** The choice itself. No label: it says what it is in its own two words. */
export function HoursMode({ partial, startTime, endTime, onChange }) {
  const set = (patch) => onChange({ partial, startTime, endTime, ...patch })
  return (
    // The system segmented control, not a pair of filled pills: one setting with two states
    // is exactly what a segmented control says, and what a pill lighting up blue does not.
    <div className="segmented hours-mode" role="group" aria-label="How much of the day">
      <button
        type="button"
        className={partial ? '' : 'selected'}
        aria-pressed={!partial}
        onClick={() => set({ partial: false, startTime: '', endTime: '' })}
      >
        All day
      </button>
      <button
        type="button"
        className={partial ? 'selected' : ''}
        aria-pressed={partial}
        onClick={() => set({ partial: true })}
      >
        Part of the day
      </button>
    </div>
  )
}

/** The window, shown only once somebody has asked for one. */
export default function HoursField({ partial, startTime, endTime, onChange, from, to }) {
  const uid = useId()
  const set = (patch) => onChange({ partial, startTime, endTime, ...patch })
  // Only the backwards case is worth a notice while typing — an end still to be chosen is
  // what the empty field already says. The same rule the dialogs disable Save on, so the
  // notice and the button can never disagree about whether the window is allowed.
  const singleDay = from === to
  const backwards =
    Boolean(startTime && endTime) && hoursWindowError({ partial, startTime, endTime }, from, to) !== null

  if (!partial) return null

  return (
    <div className="field hours-field">
      <div className="hours-row">
        {/* Each end is labelled. As two bare dropdowns they had only the group's "Hours"
            between them, so a screen reader announced the same thing twice and never said
            which was which. */}
        <span className="hours-end">
          <label className="hours-label" htmlFor={`${uid}-from`}>From</label>
          <TimeField
            id={`${uid}-from`}
            label="From"
            value={startTime || ''}
            onChange={(v) => set({ startTime: v })}
          />
        </span>
        <span className="hours-dash" aria-hidden="true">–</span>
        <span className="hours-end">
          <label className="hours-label" htmlFor={`${uid}-to`}>To</label>
          {/* On a single day the list will not offer a time before the start, which is the
              common half of the mistake the notice below catches. Typing one is still possible,
              and still told about, because a half-entered window should not be silently
              rewritten. Across a range every time is fair game: the end is on a later day. */}
          <TimeField
            id={`${uid}-to`}
            label="To"
            value={endTime || ''}
            min={singleDay ? startTime || undefined : undefined}
            onChange={(v) => set({ endTime: v })}
          />
        </span>
      </div>
      {/* Spelled out with the real dates in it, because the other reading — the same hours
          repeated on every day — is also a sentence somebody might mean, and the two describe
          completely different absences while looking identical on screen. */}
      {from !== to && (
        <span className="hint">
          {startTime && endTime
            ? `Away from ${formatTime(startTime)} on ${formatShort(from)} until ${formatTime(endTime)} on ${formatShort(to)} — one continuous absence, not these hours each day.`
            : 'One continuous absence: away from the start time on the first day until the end time on the last.'}
        </span>
      )}
      {backwards && <div className="notice">The end time is before the start time.</div>}
    </div>
  )
}
